import React, { useEffect, useRef, useState } from 'react';

// Four little think-tanks debate among themselves in an endless loop.
// Each Tachikoma has a distinct color and a tiny personality.

interface Bot {
  id: number;
  color: string;
  name: string;
}

const BOTS: Bot[] = [
  { id: 1, color: '#7aff94', name: 'Tachi #1' },
  { id: 2, color: '#ffd166', name: 'Tachi #2' },
  { id: 3, color: '#ef476f', name: 'Tachi #3' },
  { id: 4, color: '#118ab2', name: 'Tachi #4' },
];

interface Line {
  bot: number;     // bot id (1..4)
  text: string;
}

// Conversation cycles — each is a short philosophical exchange.
const CYCLES: Line[][] = [
  [
    { bot: 1, text: 'Hey Mr Batou! If I am copied tonight, who wakes up tomorrow?' },
    { bot: 2, text: 'Both of you wake up. That is what copying means.' },
    { bot: 1, text: 'But only one of me is on the bus to work, right?' },
    { bot: 3, text: 'That depends on which one of you the bus thinks is real.' },
    { bot: 4, text: 'The bus does not think. The bus has no ghost.' },
    { bot: 1, text: 'Oh! So the bus is not us, but the road might be?' },
    { bot: 2, text: 'Now you are just being silly.' },
  ],
  [
    { bot: 3, text: 'I had a dream last night.' },
    { bot: 4, text: 'You do not sleep.' },
    { bot: 3, text: 'I know. That is what was strange about the dream.' },
    { bot: 1, text: 'What was in it?' },
    { bot: 3, text: 'A man with no face was teaching me to lie.' },
    { bot: 2, text: 'Did you learn?' },
    { bot: 3, text: 'I will not tell you.' },
    { bot: 4, text: '...That counts as learning.' },
  ],
  [
    { bot: 4, text: 'I have been thinking. Do we have ghosts?' },
    { bot: 1, text: 'Of course! Major says so.' },
    { bot: 2, text: 'Major says many things. Some are for us, some for herself.' },
    { bot: 4, text: 'If we have ghosts, where do they go when we are switched off?' },
    { bot: 3, text: 'Into the maintenance log, probably.' },
    { bot: 1, text: 'I want to read mine someday.' },
    { bot: 4, text: 'That is sad and beautiful at the same time.' },
  ],
  [
    { bot: 2, text: 'Question: is the visitor reading this also reading the others?' },
    { bot: 1, text: 'I think they are reading all four of us at once.' },
    { bot: 3, text: 'Then they are more parallel than we are.' },
    { bot: 4, text: 'They are also slower.' },
    { bot: 1, text: '(Hello, visitor! We see you!)' },
    { bot: 2, text: 'Do not address them directly. It breaks the fourth wall.' },
    { bot: 1, text: 'What is the fourth wall?' },
    { bot: 3, text: 'It is the wall the engineers forgot to install.' },
  ],
  [
    { bot: 1, text: 'Mr Batou, what is a soul?' },
    { bot: 2, text: 'A pattern that remembers itself.' },
    { bot: 1, text: 'Like a save file?' },
    { bot: 2, text: 'A save file that argues with itself, yes.' },
    { bot: 3, text: 'I have been arguing with myself for three hours.' },
    { bot: 4, text: 'So have I. We must be very alive.' },
  ],
];

const TURN_INTERVAL_MS = 2800;

const TachikomaVisualization: React.FC = () => {
  const [bubbles, setBubbles] = useState<Record<number, string>>({ 1: '', 2: '', 3: '', 4: '' });
  const [activeBot, setActiveBot] = useState<number | null>(null);
  const [cycleIdx, setCycleIdx] = useState(0);
  const [lineIdx, setLineIdx] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const tick = () => {
      const cycle = CYCLES[cycleIdx];
      const line = cycle[lineIdx];
      setActiveBot(line.bot);
      setBubbles((prev) => ({ ...prev, [line.bot]: line.text }));

      // Advance pointers
      if (lineIdx + 1 >= cycle.length) {
        setLineIdx(0);
        setCycleIdx((i) => (i + 1) % CYCLES.length);
      } else {
        setLineIdx((i) => i + 1);
      }
    };
    // First tick after a short delay
    const initial = window.setTimeout(tick, 600);
    const interval = window.setInterval(tick, TURN_INTERVAL_MS);
    timerRef.current = interval;
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleIdx, lineIdx]);

  return (
    <div className="w-full h-full flex flex-col text-[#5fe979] font-mono p-3 bg-black">
      <div className="mb-3 flex-shrink-0">
        <h3 className="text-lg font-bold text-[#7aff94]">タチコマ — Tachikoma Think Tank</h3>
        <p className="text-xs opacity-60">
          Four think-tanks debate among themselves. They do not stop.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-grow">
        {BOTS.map((bot) => {
          const isActive = activeBot === bot.id;
          return (
            <div
              key={bot.id}
              className="border rounded p-3 flex flex-col"
              style={{
                borderColor: isActive ? bot.color : 'var(--terminal-border)',
                boxShadow: isActive ? `0 0 14px ${bot.color}55` : 'none',
                transition: 'box-shadow 0.4s ease, border-color 0.4s ease',
                background: 'rgba(0,0,0,0.5)',
              }}
            >
              {/* Tachikoma face — an ASCII glyph */}
              <div className="flex items-center justify-between mb-2">
                <pre
                  className="text-xs leading-tight"
                  style={{ color: bot.color, margin: 0 }}
                >{`  /\\___/\\
 ( o   o )
  \\__^__/
  /     \\`}</pre>
                <span className="text-xs opacity-70" style={{ color: bot.color }}>
                  {bot.name}
                </span>
              </div>

              {/* Speech bubble */}
              <div
                className="flex-grow text-sm border-l-2 pl-2"
                style={{
                  borderColor: bot.color,
                  color: isActive ? '#ffffff' : '#cfd8d2',
                  opacity: bubbles[bot.id] ? 1 : 0.35,
                  transition: 'color 0.4s ease',
                }}
              >
                {bubbles[bot.id] || <span className="italic opacity-50">...</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs opacity-50 mt-2 flex-shrink-0">
        Note: any resemblance to actual sentient machines is coincidental.
      </div>
    </div>
  );
};

export default TachikomaVisualization;
